import {
  type EventStreamConfig,
  getStreamConfig,
} from "@arti/dashboard-events";
/**
 * Provider progress panel showing agent events in real-time.
 *
 * Supports N event streams with dynamic layout based on the number
 * of active streams. Each stream is rendered in its own pane with
 * configurable metadata from stream:started events or the static registry.
 *
 * Features an expand button to view the panel in a larger modal.
 */
import { AnimatePresence, motion } from "framer-motion";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import type {
  DynamicStreamConfig,
  ProviderProgressEntry,
} from "../../hooks/useProviderProgress";
import type { EventTypeFilterOption } from "./providerProgress/ProviderProgressEventTypeFilter";

import { Button } from "../common/Button";
import { Modal } from "../common/Modal";
import { SectionHeader } from "../common/SectionHeader";
import {
  EventStreamPane,
  ExpandIcon,
  HIDDEN_EVENT_TYPES,
  ProviderProgressEventTypeFilter,
  deriveDisplayableProviderProgressEvents,
  type PhaseStatusData,
  ProgressIcon,
  type StreamModelInfo,
  extractLatestPhaseStatus,
  extractModelInfo,
} from "./providerProgress";

interface ProviderProgressPanelProps {
  events: ProviderProgressEntry[];
  /** Stream configs map - either dynamic (from stream:started events) or static (from registry) */
  streamConfigs: Map<string, EventStreamConfig | DynamicStreamConfig>;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

/** Group events by stream_id */
interface StreamData {
  config: EventStreamConfig | DynamicStreamConfig;
  events: ProviderProgressEntry[];
}

/** Get responsive grid classes based on number of streams */
function getGridClasses(streamCount: number, expanded: boolean): string {
  if (streamCount === 1) {
    return "grid-cols-1";
  }

  if (expanded) {
    // Expanded mode: wider modal allows more horizontal space
    if (streamCount === 2) {
      return "grid-cols-1 md:grid-cols-2";
    }
    // 3+ streams: fit all horizontally at larger breakpoints in expanded mode
    return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3";
  }

  // Collapsed mode: max 2 per row to prevent cramping
  if (streamCount === 2) {
    return "grid-cols-1 lg:grid-cols-2";
  }
  // 3+ streams: max 2 columns to ensure adequate space per agent
  return "grid-cols-1 lg:grid-cols-2";
}

export const ProviderProgressPanel: FC<ProviderProgressPanelProps> = ({
  events,
  streamConfigs,
  expanded,
  onExpandedChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[] | null>(
    null,
  );
  const isControlled = expanded !== undefined;
  const effectiveExpanded = isControlled ? expanded : isExpanded;
  const setExpanded = (next: boolean) => {
    if (isControlled) {
      onExpandedChange?.(next);
      return;
    }
    setIsExpanded(next);
  };

  // Filter to only show events that are in the display registry
  const displayableEvents = useMemo(
    () => deriveDisplayableProviderProgressEvents(events),
    [events],
  );
  const selectableEventOptions = useMemo(() => {
    const counts = new Map<string, number>();

    for (const event of displayableEvents) {
      if (HIDDEN_EVENT_TYPES.has(event.event_type)) {
        continue;
      }
      const existingCount = counts.get(event.event_type) ?? 0;
      counts.set(event.event_type, existingCount + 1);
    }

    return Array.from(
      counts,
      ([eventType, count]): EventTypeFilterOption => ({ count, eventType }),
    );
  }, [displayableEvents]);
  const selectableEventTypeKey = useMemo(
    () => selectableEventOptions.map((option) => option.eventType).join("|"),
    [selectableEventOptions],
  );
  const selectedEventTypeSet = useMemo(
    () =>
      new Set(
        selectedEventTypes ??
          selectableEventOptions.map((option) => option.eventType),
      ),
    [selectedEventTypes, selectableEventOptions],
  );
  const selectableEvents = useMemo(
    () =>
      displayableEvents.filter(
        (event) => !HIDDEN_EVENT_TYPES.has(event.event_type),
      ),
    [displayableEvents],
  );
  const totalSelectableEventCount = selectableEvents.length;
  const visibleEventCount = useMemo(
    () =>
      selectableEvents.filter((event) =>
        selectedEventTypeSet.has(event.event_type),
      ).length,
    [selectableEvents, selectedEventTypeSet],
  );
  const isEventTypeFilterActive = selectedEventTypes !== null;

  useEffect(() => {
    setSelectedEventTypes((current) => {
      if (current === null) {
        return null;
      }

      const availableTypes = new Set(
        selectableEventOptions.map((option) => option.eventType),
      );
      const next = current.filter((eventType) => availableTypes.has(eventType));
      if (next.length === selectableEventOptions.length) {
        return null;
      }
      return next;
    });
  }, [selectableEventOptions, selectableEventTypeKey]);

  const handleToggleEventType = useCallback(
    (eventType: string) => {
      setSelectedEventTypes((current) => {
        const allEventTypes = selectableEventOptions.map(
          (option) => option.eventType,
        );
        const nextSelected = new Set(current ?? allEventTypes);

        if (nextSelected.has(eventType)) {
          nextSelected.delete(eventType);
        } else {
          nextSelected.add(eventType);
        }

        const next = allEventTypes.filter((type) => nextSelected.has(type));
        return next.length === allEventTypes.length ? null : next;
      });
    },
    [selectableEventOptions],
  );

  const handleIsolateEventType = useCallback((eventType: string) => {
    setSelectedEventTypes([eventType]);
  }, []);

  const handleResetEventTypes = useCallback(() => {
    setSelectedEventTypes(null);
  }, []);

  // Group events by stream_id and get stream configs
  const streamDataList = useMemo(() => {
    // Group events by stream_id
    const eventsByStream = new Map<string, ProviderProgressEntry[]>();
    for (const event of displayableEvents) {
      const streamId = event.stream_id;
      const existing = eventsByStream.get(streamId) ?? [];
      existing.push(event);
      eventsByStream.set(streamId, existing);
    }

    // Convert to array with configs and sort by order
    const streams: StreamData[] = [];
    for (const [streamId, streamEvents] of eventsByStream) {
      // Use the provided config from streamConfigs (dynamic or static fallback)
      // Fall back to registry if not in streamConfigs map
      const config = streamConfigs.get(streamId) ?? getStreamConfig(streamId);
      streams.push({ config, events: streamEvents });
    }

    // Sort by order (from stream config)
    streams.sort((a, b) => a.config.order - b.config.order);

    return streams;
  }, [displayableEvents, streamConfigs]);

  // Extract phase status for each stream
  const phaseStatusByStream = useMemo(() => {
    const statusMap = new Map<string, PhaseStatusData | null>();
    for (const { config } of streamDataList) {
      statusMap.set(config.id, extractLatestPhaseStatus(events, config.id));
    }
    return statusMap;
  }, [events, streamDataList]);

  // Extract model info for each stream
  const modelInfoByStream = useMemo(() => {
    const infoMap = new Map<string, StreamModelInfo | null>();
    for (const { config } of streamDataList) {
      infoMap.set(config.id, extractModelInfo(events, config.id));
    }
    return infoMap;
  }, [events, streamDataList]);

  // Calculate total filtered event count (excluding hidden event types like phase:status, usage:update)
  const filteredEventCount = useMemo(
    () =>
      isEventTypeFilterActive
        ? `${visibleEventCount}/${totalSelectableEventCount} events`
        : `${visibleEventCount} events`,
    [isEventTypeFilterActive, totalSelectableEventCount, visibleEventCount],
  );

  if (displayableEvents.length === 0) {
    return null;
  }

  const streamCount = streamDataList.length;

  const expandButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => setExpanded(true)}
      className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600 dark:text-white/40 dark:hover:text-slate-300"
      aria-label="Expand provider progress"
      title="Expand"
    >
      <ExpandIcon />
    </Button>
  );

  const renderStreams = (isExpandedView: boolean) => {
    const gridClasses = getGridClasses(streamCount, isExpandedView);
    // In expanded mode, allow taller scroll areas
    const maxHeightClass = isExpandedView ? "max-h-[60vh]" : undefined;

    if (streamCount === 1) {
      const [{ config, events: streamEvents }] = streamDataList;
      return (
        <EventStreamPane
          streamConfig={config}
          events={streamEvents}
          visibleEventTypes={selectedEventTypeSet}
          isEventTypeFilterActive={isEventTypeFilterActive}
          phaseStatus={phaseStatusByStream.get(config.id)}
          modelInfo={modelInfoByStream.get(config.id)}
          maxHeightClass={maxHeightClass}
        />
      );
    }

    return (
      <div className={`grid gap-3 ${gridClasses}`}>
        {streamDataList.map(({ config, events: streamEvents }) => (
          <EventStreamPane
            key={config.id}
            streamConfig={config}
            events={streamEvents}
            visibleEventTypes={selectedEventTypeSet}
            isEventTypeFilterActive={isEventTypeFilterActive}
            phaseStatus={phaseStatusByStream.get(config.id)}
            modelInfo={modelInfoByStream.get(config.id)}
            maxHeightClass={maxHeightClass}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <section>
        <SectionHeader
          title="Provider Progress"
          variant="label"
          badge={filteredEventCount}
          icon={<ProgressIcon />}
          action={expandButton}
        />
        <ProviderProgressEventTypeFilter
          options={selectableEventOptions}
          selectedEventTypes={selectedEventTypeSet}
          onIsolate={handleIsolateEventType}
          onReset={handleResetEventTypes}
          onToggle={handleToggleEventType}
        />
        <motion.div layoutId="provider-progress-panel">
          {renderStreams(false)}
        </motion.div>
      </section>

      <AnimatePresence>
        {effectiveExpanded && (
          <Modal
            title="Provider Progress"
            subtitle={filteredEventCount}
            icon={
              <span className="flex h-8 w-8 items-center justify-center border border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
                <ProgressIcon />
              </span>
            }
            onClose={() => setExpanded(false)}
            useMotion
            overlayClassName="p-4"
            panelClassName="border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
            headerClassName="border-b border-slate-200 px-6 py-4 dark:border-slate-700"
            bodyClassName="custom-scrollbar p-6"
            widthClass="max-w-6xl"
            overlayMotionProps={{
              animate: { opacity: 1 },
              exit: { opacity: 0 },
              initial: { opacity: 0 },
              transition: { duration: 0.2 },
            }}
            panelMotionProps={{
              layoutId: "provider-progress-panel",
              transition: { layout: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
            }}
          >
            <ProviderProgressEventTypeFilter
              options={selectableEventOptions}
              selectedEventTypes={selectedEventTypeSet}
              onIsolate={handleIsolateEventType}
              onReset={handleResetEventTypes}
              onToggle={handleToggleEventType}
            />
            {renderStreams(true)}
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};
